import React from "react";
import { Button, Input, InputGroup, InputGroupText } from "reactstrap";
import { BsSearch, BsChatDots } from "react-icons/bs";
import { FilterDropdown } from "../../controls/FilterButtons";
import { getAccess } from "../../services/UserService";
import { t } from "../../services/HelperService";

export function CustomerChatToolbar(props) {
    const {
        chatIdQuery, setChatIdQuery,
        subjectQuery, setSubjectQuery,
        channelValues, setChannelValues,
        statusValues, setStatusValues,
        mineOnly, setMineOnly,
        showArchived, setShowArchived,
        unreadOnly, setUnreadOnly,
        currentRepId,
        chats,
        channelDataSource, statusDataSource,
        setDataState, fetchChats, setSelected,
        isMobile,
        onOpenNewChat
    } = props;

    return (
        <div className="ccv-toolbar p-2 d-flex gap-3 flex-wrap align-items-end">
            {/* Ref # (ChatID) */}
            <div style={{ width: 100 }}>
                <InputGroup>
                    <InputGroupText>#</InputGroupText>
                    <Input
                        value={chatIdQuery}
                        inputMode="numeric"
                        placeholder={t("application_strings.views.chat.toolbar.refPlaceholder")}
                        onChange={e => {
                            const digits = e.target.value.replace(/\D/g, "");
                            setChatIdQuery(digits);
                        }}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                const q = chatIdQuery.trim();
                                setDataState(s => ({ ...s, skip: 0, chatId: q ? Number(q) : null }));
                            }
                        }}
                    />
                </InputGroup>
            </div>

            {/* Subject contains */}
            <div style={{ width: 250 }}>
                <InputGroup>
                    <InputGroupText><BsSearch/></InputGroupText>
                    <Input
                        value={subjectQuery}
                        placeholder={t("application_strings.views.chat.toolbar.searchPlaceholder")}
                        onChange={e => setSubjectQuery(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                const q = subjectQuery.trim();
                                setSelected(null);
                                setDataState(s => ({ ...s, skip: 0, subject: q || null }));
                            }
                        }}
                    />
                </InputGroup>
            </div>

            {(
                <FilterDropdown
                    style={{ width: 150 }}
                    label={t("application_strings.views.chat.toolbar.filters.channel")}
                    value={channelValues}
                    dataSource={channelDataSource}
                    textField="Text"
                    valueField="ID"
                    onFilter={(data) => {
                        const present = new Set(chats.map(c => String(c.Channel ?? "").toLowerCase()).filter(Boolean));
                        return present.size === 0 ? data : data.filter(opt => opt.ID === "ALL" || present.has(opt.ID));
                    }}
                    onChange={({ value }) => {
                        setChannelValues(value);
                        const idsNoAll = value.filter(v => v.ID !== "ALL").map(v => v.ID);
                        setDataState(s => ({ ...s, skip: 0, channel: idsNoAll.length === 0 ? null : idsNoAll }));
                    }}
                />
            )}

            <FilterDropdown
                style={{ width: 150 }}
                label={t("application_strings.views.chat.toolbar.filters.status")}
                value={statusValues}
                dataSource={statusDataSource}
                textField="Text"
                valueField="ID"
                onFilter={(data) => {
                    const present = new Set(chats.map(c => String(c.Status ?? "").toLowerCase()).filter(Boolean));
                    return present.size === 0 ? data : data.filter(opt => opt.ID === "ALL" || present.has(opt.ID));
                }}
                onChange={({ value }) => {
                    setStatusValues(value);
                    const idsNoAll = value.filter(v => v.ID !== "ALL").map(v => v.ID);
                    setDataState(s => ({ ...s, skip: 0, status: idsNoAll.length === 0 ? null : idsNoAll }));
                }}
            />

            <Button
                color="secondary"
                outline={!mineOnly}
                active={mineOnly}
                disabled={currentRepId == null}
                onClick={() => {
                    setMineOnly(prev => {
                        const next = !prev;
                        setDataState(s => {
                            const nextState = { ...s, skip: 0, assigneeId: next ? currentRepId : null };
                            setTimeout(() => fetchChats(nextState), 0);
                            return nextState;
                        });
                        return next;
                    });
                }}
            >
                {t("application_strings.views.chat.toolbar.toggles.mine")}
            </Button>

            <Button
                color="secondary"
                outline={!unreadOnly}
                active={unreadOnly}
                onClick={() => {
                    setUnreadOnly(prev => {
                        const next = !prev;
                        setDataState(s => {
                            const nextState = { ...s, skip: 0, unreadOnly: next };
                            setTimeout(() => fetchChats(nextState), 0);
                            return nextState;
                        });
                        return next;
                    });
                }}
                title={t("application_strings.views.chat.toolbar.toggles.unreadTooltip")}
            >
                {t("application_strings.views.chat.toolbar.toggles.unread")}
            </Button>

            {getAccess().features().FeatureCanViewArchived() && (
                <Button
                    color="secondary"
                    outline={!showArchived}
                    active={showArchived}
                    onClick={() => {
                        setShowArchived(prev => {
                            const next = !prev;
                            setSelected(null);
                            setDataState(s => {
                                const nextState = { ...s, skip: 0, isArchived: next };
                                setTimeout(() => fetchChats(nextState), 0);
                                return nextState;
                            });
                            return next;
                        });
                    }}
                    title={t("application_strings.views.chat.toolbar.toggles.archivedTooltip")}
                >
                    {t("application_strings.views.chat.toolbar.toggles.archived")}
                </Button>
            )}
            {!isMobile && (
                <div className="ms-auto">
                    <Button
                        color="primary"
                        className="ccv-newchat-btn"
                        onClick={onOpenNewChat}
                        title={t("application_strings.views.chat.actions.newChat")}
                    >
                        <BsChatDots className="me-2" />
                        {t("application_strings.views.chat.actions.newChat")}
                    </Button>
                </div>
            )}
        </div>
    );
}
